import {
  filterBySeasonId,
  filterByLocationId,
  filterByIds
} from '@/util/filters'

const filterFunctions = {
  districts: (values) => (item) => values.includes(item.district),
  seasons: (values) => (item) => values.includes(item.seasonId),
  location: (value) => (item) => value === item.locationId,
  search: (value) => (item) => value && item
}

function isValidFilter([name, values]) {
  return filterFunctions[name] && values.length
}

export default {
  /**
   * Raw data getters, for clarity
   */
  allProductions: (state) => state.productions,
  allSeasons: (state) => state.seasons,
  allLocations: (state) => state.locations,
  allDistricts: (state) => state.districts,

  /**
   * Check if form filters are being applied,
   * i.e. yes, unless searching or location selected directly
   */
  isApplyingFormFilters(state) {
    const { search, location } = state.filters
    return !search && !location
  },

  /**
   * Productions, filtered by current user filters
   */
  productions(state) {
    if (state.loading) return []

    const allProductions = state.productions
    const { search, location, ...filters } = state.filters

    // Search: only apply search query, bail early
    if (search) {
      return allProductions.filter(filterFunctions.search(search))
    }
    // Location: only apply location filter, bail early
    if (location) {
      return allProductions.filter(filterFunctions.location(location))
    }

    // All other filters: filter simultaneously
    const validFilters = Object.entries(filters).filter(isValidFilter)
    return validFilters.reduce((items, [filterName, filterValues]) => {
      // Get named filter function from above
      const filterFunc = filterFunctions[filterName](filterValues)
      return items.filter(filterFunc)
    }, allProductions)
  },

  /**
   * Seasons, filtered by current user filters
   */
  seasons(state, getters) {
    const seasonIds = getters.productions.map((p) => p.seasonId)
    return filterByIds(state.seasons, seasonIds)
  },

  /**
   * Locations, filtered by current user filters
   */
  locations(state, getters) {
    const locationIds = getters.productions.map((p) => p.locationId)
    return filterByIds(state.locations, locationIds)
  },

  /**
   * Districts, filtered by selected season
   */
  districts(state) {
    let districts
    if (state.filters.seasons.length) {
      districts = state.filters.seasons.reduce((districts, seasonId) => {
        const productions = filterBySeasonId(state.productions, seasonId)
        const locationIds = productions.map((p) => p.locationId)
        const locations = filterByIds(state.locations, locationIds)
        return districts.concat(locations.map((l) => l.district))
      }, [])
    } else {
      districts = state.locations.map((p) => p.district)
    }
    const uniqueDistricts = [...new Set(districts)].sort()
    return uniqueDistricts
  },

  /**
   * All results, grouped hierarchically
   * Seasons > Locations > Productions
   */
  productionsByLocationBySeason(state, getters) {
    return getters.seasons.map((season) => {
      const productions = filterBySeasonId(getters.productions, season._id)
      const locationIds = productions.map((p) => p.locationId)
      const locations = filterByIds(getters.locations, locationIds)
      const districts = [...new Set(locations.map((l) => l.district))]
      return {
        ...season,
        districtCount: districts.length,
        productionCount: productions.length,
        locationCount: locations.length,
        locations: locations.map((location) => {
          const locationProductions = filterByLocationId(
            productions,
            location._id
          )
          return {
            ...location,
            productionCount: productions.length,
            productions: locationProductions
          }
        })
      }
    })
  },

  isLocationHighlighted(state) {
    return (location) => state.highlightedLocation === location._id
  }
}
